import React, { FC, useEffect, useMemo, useState } from 'react';
import {
    Button, TextField, Grid, Autocomplete,
    Dialog, DialogContent, DialogTitle, DialogActions,
    IconButton, Box, Checkbox, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Typography, InputAdornment, Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import { z } from 'zod';
import { PaperComponent } from 'shared/ui/draggable/Draggable';
import { AlertCustom } from 'shared/ui/alert/AlertCustom';
import { ScheduleService } from 'services/ScheduleService';
import { ShiftProfile } from 'repository/schedule/@types/ShiftProfile';
import {
    createShiftProfileGroupSchema,
    ShiftProfileGroupFormValues,
    EMPTY_GROUP_ITEM,
} from 'schemas/ShiftProfileGroup';

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: any[];
    initialValues?: Partial<ShiftProfileGroupFormValues>;
    titleKey: string;
    submitLabel: string;
    onSubmit: (values: ShiftProfileGroupFormValues) => Promise<void> | void;
    readOnly?: boolean;
}

const DAYS: { key: keyof Pick<typeof EMPTY_GROUP_ITEM, 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'>; label: string }[] = [
    { key: 'monday', label: 'Lun' },
    { key: 'tuesday', label: 'Mar' },
    { key: 'wednesday', label: 'Mier' },
    { key: 'thursday', label: 'Jue' },
    { key: 'friday', label: 'Vie' },
    { key: 'saturday', label: 'Sab' },
    { key: 'sunday', label: 'Dom' },
];

const scheduleService = new ScheduleService();

const buildDetails = (profile?: ShiftProfile): string => {
    if (!profile) return '';
    const parts: string[] = [];
    if (profile.min_start_time || profile.max_start_time) {
        parts.push(`Inicio ${profile.min_start_time ?? '--:--'}–${profile.max_start_time ?? '--:--'}`);
    }
    if (profile.min_end_time || profile.max_end_time) {
        parts.push(`Fin ${profile.min_end_time ?? '--:--'}–${profile.max_end_time ?? '--:--'}`);
    }
    if (profile.standard_duration) {
        parts.push(`Duración ${profile.standard_duration}`);
    }
    return parts.join(' · ');
};

export const FormShiftProfileGroup: FC<Props> = ({
    open, setOpen, rows, initialValues, titleKey, submitLabel, onSubmit, readOnly = false,
}) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [alerts, setAlerts] = React.useState(false);
    const [profiles, setProfiles] = useState<ShiftProfile[]>([]);
    const [loadingProfiles, setLoadingProfiles] = useState(false);

    const loadProfiles = React.useCallback(async () => {
        setLoadingProfiles(true);
        try {
            const data = await scheduleService.shift_profile.getAll();
            setProfiles(data);
        } catch (err) {
            console.error('Error cargando perfiles de turno:', err);
        } finally {
            setLoadingProfiles(false);
        }
    }, []);

    useEffect(() => {
        if (open) loadProfiles();
    }, [open, loadProfiles]);

    const defaultValues: ShiftProfileGroupFormValues = useMemo(() => ({
        name: initialValues?.name ?? '',
        description: initialValues?.description ?? '',
        items: initialValues?.items && initialValues.items.length > 0
            ? initialValues.items.map((it) => ({ ...EMPTY_GROUP_ITEM, ...it }))
            : [{ ...EMPTY_GROUP_ITEM }],
    }), [initialValues]);

    const schema = useMemo(
        () => createShiftProfileGroupSchema({ t, rows, initialValues, readOnly }),
        [t, rows, initialValues, readOnly],
    );

    type SchemaType = z.infer<ReturnType<typeof createShiftProfileGroupSchema>>;

    const {
        control, register, reset, handleSubmit, watch,
        formState: { errors, isSubmitting, isDirty, isValid },
    } = useForm<SchemaType>({
        defaultValues,
        resolver: zodResolver(schema as any),
        mode: 'onChange',
    });

    const { fields, append, remove } = useFieldArray({ control, name: 'items' });

    useEffect(() => {
        if (open) reset(defaultValues);
    }, [open, defaultValues, reset]);

    const changes = () => {
        if (isDirty) setAlerts(true);
        else setOpen(false);
    };

    const closeForm = () => {
        setOpen(false);
        setAlerts(false);
    };

    const onSubmitForm = handleSubmit(async (values) => {
        try {
            await onSubmit(values as ShiftProfileGroupFormValues);
            closeForm();
        } catch (err) {
            console.error(err);
            enqueueSnackbar(t('general.errors.unexpected'), { variant: 'error' });
        }
    });

    const nameWatch = watch('name') ?? '';
    const itemsWatch = watch('items') ?? [];

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') return;
                changes();
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="lg"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t(titleKey, { defaultValue: titleKey })}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <AlertCustom alerts={alerts} setAlerts={setAlerts} closeForm={closeForm} t={t} />
                )}
            </Box>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            required
                            label="Nombre"
                            placeholder="Ingrese nombre"
                            autoComplete="off"
                            variant="outlined"
                            {...register('name')}
                            error={Boolean(errors.name)}
                            helperText={errors.name?.message ?? ''}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">{`${nameWatch.length}/100`}</InputAdornment>
                                    ),
                                },
                                htmlInput: { maxLength: 100 },
                            }}
                            disabled={readOnly}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Descripción"
                            placeholder="Ingrese descripción"
                            autoComplete="off"
                            variant="outlined"
                            {...register('description')}
                            error={Boolean(errors.description)}
                            helperText={errors.description?.message ?? ''}
                            slotProps={{ htmlInput: { maxLength: 255 } }}
                            disabled={readOnly}
                        />
                    </Grid>
                </Grid>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 40 }} />
                                <TableCell sx={{ minWidth: 240 }}>Perfil</TableCell>
                                <TableCell sx={{ minWidth: 240 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span>Detalles</span>
                                        <Tooltip title="Recargar perfiles">
                                            <span>
                                                <IconButton size="small" onClick={loadProfiles} disabled={loadingProfiles}>
                                                    <ReplayIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                {DAYS.map((d) => (
                                    <TableCell key={d.key} align="center">{d.label}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fields.map((field, index) => {
                                const currentProfileId = itemsWatch[index]?.shift_profile_id ?? null;
                                const currentProfile = profiles.find((p) => p.shift_profile_id === currentProfileId);
                                const itemErrors: any = errors.items?.[index];
                                return (
                                    <TableRow key={field.id}>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                color="default"
                                                onClick={() => remove(index)}
                                                disabled={readOnly || fields.length === 1}
                                                aria-label="quitar"
                                            >
                                                <RemoveCircleOutlineIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <Controller
                                                name={`items.${index}.shift_profile_id` as const}
                                                control={control}
                                                render={({ field: ctrl }) => (
                                                    <Autocomplete
                                                        size="small"
                                                        options={profiles}
                                                        loading={loadingProfiles}
                                                        getOptionLabel={(opt) => opt?.shift_profile_name ?? ''}
                                                        isOptionEqualToValue={(opt, val) => opt.shift_profile_id === val?.shift_profile_id}
                                                        value={profiles.find((p) => p.shift_profile_id === ctrl.value) ?? null}
                                                        onChange={(_, newValue) => ctrl.onChange(newValue?.shift_profile_id ?? null)}
                                                        disabled={readOnly}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                placeholder="Seleccionar perfil"
                                                                error={Boolean(itemErrors?.shift_profile_id)}
                                                                helperText={itemErrors?.shift_profile_id?.message ?? ''}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {buildDetails(currentProfile)}
                                            </Typography>
                                        </TableCell>
                                        {DAYS.map((d) => (
                                            <TableCell key={d.key} align="center">
                                                <Controller
                                                    name={`items.${index}.${d.key}` as const}
                                                    control={control}
                                                    render={({ field: ctrl }) => (
                                                        <Checkbox
                                                            size="small"
                                                            checked={!!ctrl.value}
                                                            onChange={(e) => ctrl.onChange(e.target.checked)}
                                                            disabled={readOnly}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {typeof errors.items?.message === 'string' && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {errors.items.message as string}
                    </Typography>
                )}

                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => append({ ...EMPTY_GROUP_ITEM })}
                        disabled={readOnly}
                    >
                        Agregar
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                {!readOnly && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onSubmitForm}
                        loading={isSubmitting}
                        disabled={!isValid || isSubmitting}
                    >
                        {submitLabel}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
